import WidgetTitle from "../WidgetTitle/WidgetTitle";
import { Grid } from "@material-ui/core";
import { leadTimeWidgetStyle } from "./style";
import _ from "lodash";
import { useMemo } from "react";
import moment from "moment";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useSelector } from "react-redux";
import LeadTimeStatisics from "./LeadTimeStatisics";
import ExportButton from "components/ExportButton/ExportButton";
import { IconDownloadFile } from "icons";
import { dateFilterTypes } from "../loadsDashboardConsts";
import { loadStatuses } from "features/loads/enums";

const LeadTimeWidget = ({ loads, loading }) => {
    const classes = leadTimeWidgetStyle();

    const { dashboardFilters } = useSelector(state => state.loadsDashboardState);

    const isCancelled = load => [loadStatuses.CANCELLED, loadStatuses.CANCELLED_PARQ].includes(load.latestStatus);

    let loadsFiltered = loads?.filter(l => l?.acceptedDate !== null && !l.ignoreForSla && !isCancelled(l));

    const isByDay = dashboardFilters['dateFilterType'].value < dateFilterTypes.QUARTER;
    const key = isByDay ? 'day' : 'month';
    const format = isByDay ? "YYYY-MM-DD" : "YYYY-MM";

    const leadTimes = loadsFiltered.map(load => {
        const createdDate = moment(load.createdDate);
        const acceptedDate = moment(load.acceptedDate);
        return {
            ...load,
            key: acceptedDate.format(format),
            month: acceptedDate.format("YYYY-MM"),
            day: acceptedDate.format("YYYY-MM-DD"),
            leadTime: acceptedDate.diff(createdDate, "hours")
        };
    });

    const getStatistics = (groupLoads, key) => {
        const totalLeadTime = groupLoads?.reduce((sum, load) => sum + load.leadTime, 0);
        const averageLeadTime = groupLoads?.length ? totalLeadTime / groupLoads?.length : 0;

        const variance = groupLoads?.reduce((sum, load) => sum + Math.pow(load.leadTime - averageLeadTime, 2), 0) / (groupLoads?.length || 1);
        const standardDeviation = Math.sqrt(variance);
        return {
            monthLoads: groupLoads,
            month: key,
            averageLeadTime: +averageLeadTime?.toFixed(2),
            standardDeviation: +standardDeviation?.toFixed(2),
            monthName: moment(key).format(isByDay ? "MM/DD" : "MMM"),
            year: moment(key).format("YYYY"),
        };
    }

    const averageLeadTime = useMemo(() => {
        if (!loadsFiltered || loadsFiltered.length === 0) return [];
        const groupedByKey = _.groupBy(leadTimes, "key");

        const start = moment(dashboardFilters['startDate'].value).startOf(key);
        const end = moment(dashboardFilters['endDate']?.value).startOf(key);
        const keys = [];
        let currentKey = start;

        while (currentKey <= end) {
            keys.push(currentKey.format(format));
            currentKey = currentKey.add(1, key);
        }

        const result = keys.map(item => {
            const groupLoads = groupedByKey[item] || [];
            return getStatistics(groupLoads, item);
        });

        return result;
    }, [loads]);

    const lastMonthsData = useMemo(() => {
        if (!loadsFiltered || loadsFiltered.length === 0) return [];
        const groupedByKey = _.groupBy(leadTimes, "month");
        const names = {
            1: 'The Month Before Last',
            2: 'Last Month',
            3: 'This Month',
        }

        let result = [];
        const start = moment(dashboardFilters['startDate'].value).startOf('month');
        const end = moment(dashboardFilters['endDate']?.value).startOf('month');
        let month = moment().subtract(2, 'month').startOf('month')//.format("YYYY-MM")
        let i = 0;
        while (month <= end && i < 3) {
            let monthStr = month.format("YYYY-MM");
            if (month >= start) {
                result.push({
                    ...getStatistics(groupedByKey[monthStr], monthStr),
                    order: i + 1,
                    name: names[i + 1],
                });
            }
            month = moment(month).add(1, 'month');
            i++;
        }

        return result

    }, [loads]);


    const CustomTooltip = ({ payload }) => {
        return (
            <div className={classes.graphTootltip}>
                <p>
                    <div><b>{payload?.[0]?.payload?.monthName} {payload?.[0]?.payload?.year}</b></div>
                    <div><b>Average: </b>{payload?.[0]?.payload?.averageLeadTime}h</div>
                    {/* <div><b>Standard Deviation: </b>{payload?.[0]?.payload?.standardDeviation.toFixed(2)}h</div> */}
                </p>
            </div>
        );
    };

    const mapExportData = (a => {
        return {
            ['Time Period']: `${a.monthName} ${a.year}`,
            ['Average']: `${a.averageLeadTime}`,
            ['Standard Deviation']: `${a.standardDeviation}`
        };
    })

    return <Grid container direction="column" className={classes.graphWrapper}>
        <Grid item>
            <WidgetTitle
                title='Lead Time'
                loading={loading}
            />
        </Grid>
        <div style={{ position: 'absolute', top: '1.3em', right: '0.5em' }}>
            <ExportButton
                btnStyle='text'
                label=""
                icon={<IconDownloadFile />}
                fileLabel="Export"
                data={averageLeadTime}
                mapExportData={mapExportData}
            />
        </div>
        <LeadTimeStatisics lastMonthsData={lastMonthsData} />
        <ResponsiveContainer width="95%" height="60%">
            <AreaChart
                data={averageLeadTime}
            >
                <XAxis
                    dataKey="monthName"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                        color: 'darkGray',
                        fontSize: '0.7em',
                        fontWeight: '600',
                        dy: 4,
                    }}
                />
                <YAxis
                    dataKey="averageLeadTime"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                        color: 'darkGray',
                        fontSize: '0.7em',
                        fontWeight: '600',
                        dx: -10,
                    }}
                    tickFormatter={amount => {
                        // if (amount > 168) {
                        //     return `${Math.floor(amount / 168)}w ${Math.floor((amount % 168) / 24)}d`
                        // }
                        if (amount > 24) {
                            return `${Math.floor(amount / 24)}d ${amount % 24}h`
                        }
                        return `${amount}h`
                    }}
                />
                <Tooltip content={<CustomTooltip />} />
                <defs>
                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <Area type="monotone" dataKey="averageLeadTime" stackId="1" stroke="#82ca9d" fill="url(#colorUv)" />
                {/* <Area type="monotone" dataKey="standardDeviation" stackId="1" stroke="#82ca9d" fill="url(#colorUv)" /> */}
            </AreaChart>
        </ResponsiveContainer>
    </Grid>
}
export default LeadTimeWidget;